import React from 'react';
import { useTranslation } from 'react-i18next';

import { useScrollIntoViewOnOpen } from '../../hooks/useScrollIntoViewOnOpen';
import type { IFilterOption } from '../../types/filter.types';
import { FilterChip } from './FilterChip';
import styles from './FilterPanel.module.css';

const DropArrowIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg" width="13" height="8" viewBox="0 0 13 8" fill="none" className={`${styles.accordionArrow} ${isOpen ? styles.accordionArrowOpen : ''}`}>
    <path d="M0.984376 0.984374L6.23438 6.98437L11.4844 0.984375" stroke="#D9D9D9" strokeWidth="1.96875" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface AccordionProps {
  label: string;
  isOpen?: boolean;
  onClick?: () => void;
  options?: IFilterOption[];
  onOptionSelect?: (optionId: string) => void;
  isOptionSelected?: (optionId: string) => boolean;
  hasActiveFilters?: boolean;
}

export const Accordion: React.FC<AccordionProps> = ({
  label,
  isOpen = false,
  onClick,
  options = [],
  onOptionSelect,
  isOptionSelected,
  hasActiveFilters = false,
}) => {
  const { t } = useTranslation();
  const wrapperRef = useScrollIntoViewOnOpen<HTMLDivElement>(isOpen);

  // Get selected options to show inline when closed
  const selectedOptions = options.filter((option) => isOptionSelected?.(option.id));

  return (
    <div ref={wrapperRef} className={`${styles.accordionWrapper} ${isOpen ? styles.accordionWrapperOpen : ''}`}>
      <div
        className={styles.accordionItem}
        onClick={onClick}
      >
        <span className={hasActiveFilters ? styles.accordionLabelActive : ''}>{label}</span>
        {!isOpen && selectedOptions.length > 0 && (
          <div className={styles.inlineChipsContainer}>
            {selectedOptions.map((option) => (
              <span key={option.id} className={styles.inlineChip}>
                {option.labelKey ? t(option.labelKey, option.label) : option.label}
              </span>
            ))}
          </div>
        )}
        <DropArrowIcon isOpen={isOpen} />
      </div>
      {options.length > 0 && (
        <div className={`${styles.accordionContent} ${isOpen ? styles.accordionContentOpen : ''}`}>
          <div className={styles.filterChipsContainer}>
            {options.map((option) => (
              <FilterChip
                key={option.id}
                label={option.labelKey ? t(option.labelKey, option.label) : option.label}
                isSelected={isOptionSelected?.(option.id)}
                onClick={() => onOptionSelect?.(option.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
